import { assert } from '@ember/debug';
import Service, { service } from '@ember/service';

import { task } from 'ember-concurrency';

import { apiBaseURL } from 'register-partners-js/constants/hosts';

export default class CheckoutService extends Service {
  @service networkManager;

  /**
   * https://openapi-master.staging.qonto-cbs.co/biller#tag/RegistrationProductSelection
   * ⚠️ Will return 404 if `this.savePricePlan` has not been called before.
   */
  fetchPricePlanSelectedTask = task(async orgId => {
    assert('Required parameter: organizationId', orgId);

    return await this.networkManager.request(
      `${apiBaseURL}/v1/registration_product_selections/${orgId}`
    );
  });

  /**
   * https://openapi-master.staging.qonto-cbs.co/pricing-catalog#tag/PricePlan/operation/v1/price_plan_offers/register_existing_company
   */
  fetchPricePlansForExistingCompanyTask = task(async ({ legalCountry, partnerName }) => {
    assert('Required parameter: legalCountry', legalCountry);

    let url = new URL('/v1/product_offers/register_existing_company', apiBaseURL);
    url.searchParams.append('legal_country', legalCountry);
    url.searchParams.append('include_decoration', 'true');

    if (partnerName) {
      url.searchParams.append('partner_name', partnerName);
    }

    let { products, banner } = await this.networkManager.request(url.toString());

    return {
      banner,
      products: products.reduce((acc, curr) => {
        if (curr.lineup === 'solo') {
          acc.push({ ...curr, add_ons: [] });
        } else {
          acc.push({
            ...curr,
            add_ons: curr.add_ons.filter(
              ({ code }) => ['accounts_payable', 'expense_spend_management'].includes(code) || !code
            ),
          });
        }

        return acc;
      }, []),
    };
  });

  /**
   * https://openapi-master.staging.qonto-cbs.co/pricing-catalog#tag/Product-Offers/operation/GetCompanyCreationProductOffers
   */
  fetchPricePlansForCompanyCreationTask = task(async ({ legalCountry, partnerName }) => {
    assert('Required parameter: legalCountry', legalCountry);

    let url = new URL('/v1/product_offers/register_company_creation', apiBaseURL);
    url.searchParams.append('legal_country', legalCountry);
    url.searchParams.append('include_decoration', 'true');

    if (partnerName) {
      url.searchParams.append('partner_name', partnerName);
    }

    return await this.networkManager.request(url.toString());
  });

  /**
   * https://openapi-master.staging.qonto-cbs.co/pricing-catalog#tag/Product-Offers/operation/EstimateExistingCompany
   */
  fetchPriceEstimateForExistingCompanyTask = task(
    async ({ legalCountry, productId, recurrence }) => {
      assert('Required property: legalCountry', legalCountry);
      assert('Required property: productId', productId);
      assert('Required property: recurrence', recurrence);

      let payload = {
        legal_country: legalCountry,
        core_product: {
          product_id: productId,
          subscription_recurrence: recurrence,
        },
        addon_products: [],
      };

      return await this.networkManager.request(
        `${apiBaseURL}/v1/product_offers/estimate_existing_company`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
    }
  );

  /**
   * https://openapi-master.staging.qonto-cbs.co/pricing-catalog#tag/Product-Offers/operation/EstimateCompanyCreation
   */
  fetchPriceEstimateForCompanyCreationTask = task(
    async ({ legalCountry, productId, recurrence }) => {
      assert('Required property: legalCountry', legalCountry);
      assert('Required property: productId', productId);
      assert('Required property: recurrence', recurrence);

      let payload = {
        legal_country: legalCountry,
        core_product: {
          product_id: productId,
          subscription_recurrence: recurrence,
        },
        addon_products: [],
      };

      return await this.networkManager.request(
        `${apiBaseURL}/v1/product_offers/estimate_company_creation`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
    }
  );

  /**
   * @param {string} organizationId
   * @param {string} planId
   * @param {string} planCode
   * @param {'monthly' | 'annual'} recurrence
   * @param {'existing_company' | 'company_creation'} flow
   * @param {string} legalCountry
   * @param {string} [partnerName]
   * @returns {Promise<"https://openapi-master.staging.qonto-cbs.co/biller#tag/RegistrationProductSelection">} registration
   */
  async savePricePlan({
    organizationId,
    planId,
    planCode,
    recurrence,
    flow,
    legalCountry,
    partnerName,
  }) {
    assert('Required property: organizationId', organizationId);
    assert('Required property: planId', planId);
    assert('Required property: planCode', planCode);
    assert(
      'Required property: recurrence, must be one of "monthly" or "annual"',
      ['monthly', 'annual'].includes(recurrence)
    );
    assert(
      'Required property: flow, must be one of "existing_company" or "company_creation"',
      ['existing_company', 'company_creation'].includes(flow)
    );
    assert('Required property: legalCountry', legalCountry);

    let product = {
      product_id: planId,
      product_kind: 'core',
      code: planCode,
      recurrence,
    };

    await this.networkManager.request(
      `${apiBaseURL}/v1/registration_product_selections/${organizationId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          registration_flow_type: flow,
          legal_country: legalCountry,
          selected_products: [product],
          partner_name: partnerName,
        }),
      }
    );
  }
}
